import { Component, OnInit, OnDestroy, signal, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ProjectDataService } from '../../services/project-data.service';
import { FirestoreProjectService } from '../../services/firestore-project.service';
import { AuthService } from '../../services/auth.service';
import { PhotoUploadService } from '../../services/photo-upload.service';
import { DepartmentSchemesService } from '../../services/department-schemes.service';
import { UserRole } from '../../models/user.model';
import { Project } from '../../models/project.model';
import { PhotoUploadComponent } from '../../components/photo-upload/photo-upload.component';
import {
    OPERATOR_LOCATION_BLOCKS,
    NAC_BLOCKS,
    GP_BY_BLOCK,
} from '../../constants/operator-master-data';

@Component({
    selector: 'app-project-detail',
    imports: [CommonModule, ReactiveFormsModule, PhotoUploadComponent],
    templateUrl: './project-detail.component.html',
    styleUrls: ['./project-detail.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectDetailComponent implements OnInit, OnDestroy {
    project           = signal<Project | undefined>(undefined);
    isEditMode        = signal(false);
    showDeleteConfirm = signal(false);
    isSaving          = signal(false);
    isDeleting        = signal(false);
    saveError         = signal<string | null>(null);
    isFirestoreDoc    = signal(false);
    photoSaveError    = signal<string | null>(null);
    saveSuccess       = signal(false);

    // Lightbox
    lightboxSrc = signal<string | null>(null);

    editForm!: FormGroup;

    private projectId = '';
    private destroy$  = new Subject<void>();

    private route            = inject(ActivatedRoute);
    private router           = inject(Router);
    private location         = inject(Location);
    private projectService   = inject(ProjectDataService);
    private firestoreService = inject(FirestoreProjectService);
    private photoUploadService = inject(PhotoUploadService);
    private deptSchemesService = inject(DepartmentSchemesService);
    private fb               = inject(FormBuilder);
    private authService      = inject(AuthService);

    // ── Role helpers ──────────────────────────────────────────────────────
    canEdit = computed(() => {
        const role = this.authService.currentUser()?.role;
        return role === UserRole.MINISTER || role === UserRole.ADMIN || role === UserRole.OSD;
    });

    /** Only Operator and Admin may upload/remove photos. OSD and Minister are view/review roles. */
    canUploadPhotos = computed(() => {
        const role = this.authService.currentUser()?.role;
        return role === UserRole.OPERATOR || role === UserRole.ADMIN;
    });

    isOsd = computed(() => this.authService.isOsd());

    // ── Static options ────────────────────────────────────────────────────
    readonly statusOptions: Array<Project['status']> = ['Planned', 'In Progress', 'Completed', 'Stuck'];
    readonly locationBlocks = OPERATOR_LOCATION_BLOCKS;

    // ── Edit form — block/GP reactivity ──────────────────────────────────
    selectedFormBlock = signal('');

    gpOptions = computed<string[]>(() => GP_BY_BLOCK[this.selectedFormBlock()] ?? []);
    isNacBlock = computed(() => NAC_BLOCKS.has(this.selectedFormBlock()));

    /** When editing, the project's own year may fall outside the dropdown's normal range. */
    editExtraYear = signal<number | null>(null);

    /** Year dropdown: 2026 down to 2020, plus the edited project's own year if outside that range. */
    yearOptions = computed<number[]>(() => {
        const years: number[] = [];
        for (let y = 2026; y >= 2020; y--) years.push(y);
        const extra = this.editExtraYear();
        if (extra && !years.includes(extra)) years.unshift(extra);
        return years;
    });

    /** Scheme options derived from the project's department. */
    osdSchemeOptions = computed<string[]>(() => {
        const dept = this.project()?.dept;
        if (!dept) return [];
        const entry = this.deptSchemesService.getDepartmentByName(dept);
        return entry?.schemes.map(s => s.name) ?? [];
    });

    // ── Edit form — photo state ───────────────────────────────────────────
    photoBeforeFiles    = signal<File[]>([]);
    photoDuringFiles    = signal<File[]>([]);
    photoAfterFiles     = signal<File[]>([]);
    photoBeforeDataUrls = signal<string[]>([]);
    photoDuringDataUrls = signal<string[]>([]);
    photoAfterDataUrls  = signal<string[]>([]);
    photoBeforeExisting = signal<string[]>([]);
    photoDuringExisting = signal<string[]>([]);
    photoAfterExisting  = signal<string[]>([]);
    isUploadingPhotos   = signal(false);
    photoUploadProgress = signal(0);

    photoBeforePreviews = computed(() => [...this.photoBeforeExisting(), ...this.photoBeforeDataUrls()]);
    photoDuringPreviews = computed(() => [...this.photoDuringExisting(), ...this.photoDuringDataUrls()]);
    photoAfterPreviews  = computed(() => [...this.photoAfterExisting(), ...this.photoAfterDataUrls()]);

    // ── View mode photo computed helpers ──────────────────────────────────
    photoBefore  = computed(() => this.project()?.projectPhotos?.before ?? []);
    photoDuring  = computed(() => this.project()?.projectPhotos?.during ?? []);
    photoAfter   = computed(() => this.project()?.projectPhotos?.after  ?? []);

    photoDateBefore = computed(() => this.project()?.photoDates?.before ?? null);
    photoDateDuring = computed(() => this.project()?.photoDates?.during ?? null);
    photoDateAfter  = computed(() => this.project()?.photoDates?.after  ?? null);

    photosLegacy = computed(() => {
        const p = this.project();
        if (!p) return [];
        const structured = (p.projectPhotos?.before?.length ?? 0)
                         + (p.projectPhotos?.during?.length ?? 0)
                         + (p.projectPhotos?.after?.length  ?? 0);
        return structured > 0 ? [] : (p.photos ?? []);
    });

    firstBefore = computed(() => this.photoBefore()[0] ?? this.photosLegacy()[0] ?? null);
    firstDuring = computed(() => this.photoDuring()[0] ?? null);
    firstAfter  = computed(() => this.photoAfter()[0]  ?? null);

    countBefore = computed(() => this.photoBefore().length || this.photosLegacy().length);
    countDuring = computed(() => this.photoDuring().length);
    countAfter  = computed(() => this.photoAfter().length);

    // ── Executant / year / GP display helpers ─────────────────────────────
    executantName    = computed(() => this.project()?.executorName    || this.project()?.executant?.name    || '');
    executantContact = computed(() => this.project()?.executorNumber  || this.project()?.executant?.contact || '');
    projectYear      = computed(() => this.project()?.yearOfCreation  || this.project()?.sanctionedYear    || '');
    gpDisplay        = computed(() => {
        const p = this.project() as any;
        return p?.gp || p?.gpName || '';
    });

    // ── Status badge ──────────────────────────────────────────────────────
    getStatusClass(status: string): string {
        switch (status) {
            case 'Completed': return 'badge-completed';
            case 'Stuck':     return 'badge-stuck';
            case 'Planned':   return 'badge-planned';
            default:          return 'badge-inprogress';
        }
    }

    // ── Timestamp formatter ───────────────────────────────────────────────
    formatTs(ts: any): string {
        if (!ts) return '—';
        try {
            const d = ts?.toDate ? ts.toDate() : new Date(ts);
            return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch { return '—'; }
    }

    // ── Init ──────────────────────────────────────────────────────────────
    ngOnInit(): void {
        this.projectId = this.route.snapshot.paramMap.get('id') ?? '';
        if (!this.projectId) return;

        this.firestoreService.getProjectById$(this.projectId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: fp => {
                    if (fp) {
                        this.project.set(fp);
                        this.isFirestoreDoc.set(true);
                    } else {
                        this.project.set(this.projectService.getProjectById(this.projectId));
                        this.isFirestoreDoc.set(false);
                    }
                },
                error: err => {
                    console.error('Failed to load project:', err);
                    this.project.set(this.projectService.getProjectById(this.projectId));
                    this.isFirestoreDoc.set(false);
                }
            });
    }

    // ── Lightbox ──────────────────────────────────────────────────────────
    openLightbox(src: string): void { this.lightboxSrc.set(src); }
    closeLightbox(): void           { this.lightboxSrc.set(null); }

    // ── Block change ──────────────────────────────────────────────────────
    onBlockChange(block: string): void {
        this.selectedFormBlock.set(block);
        const gpCtrl = this.editForm.get('gp');
        if (NAC_BLOCKS.has(block)) {
            gpCtrl?.setValue('NAC');
            gpCtrl?.disable();
        } else {
            gpCtrl?.enable();
            gpCtrl?.setValue('');
        }
    }

    // ── Edit form ─────────────────────────────────────────────────────────
    openEditForm(): void {
        const p = this.project();
        if (!p) return;

        const block = (p as any).block || p.loc || '';
        this.selectedFormBlock.set(block);
        this.editExtraYear.set(Number((p as any).yearOfCreation ?? (p as any).sanctionedYear) || null);

        this.editForm = this.fb.group({
            name:                  [p.name,     [Validators.required, Validators.minLength(3)]],
            scheme:                [p.scheme    ?? ''],
            block:                 [block],
            gp:                    [(p as any).gp ?? ''],
            status:                [p.status,   Validators.required],
            cost:                  [p.cost,     [Validators.required, Validators.min(0)]],
            spent:                 [p.spent,    [Validators.required, Validators.min(0)]],
            yearOfCreation:        [(p as any).yearOfCreation ?? (p as any).sanctionedYear ?? ''],
            roadLength:            [p.roadLength ?? null],
            executantName:         [p.executorName ?? p.executant?.name ?? ''],
            executantContact:      [p.executorNumber ?? p.executant?.contact ?? ''],
            hmPriority:            [!!p.hmPriority],
            recurringIntervention: [!!p.recurringIntervention],
            isDelayed:             [!!(p as any).isDelayed],
            delayedReason:         [(p as any).delayedReason ?? ''],
            photoDateBefore:       [p.photoDates?.before ?? ''],
            photoDateDuring:       [p.photoDates?.during ?? ''],
            photoDateAfter:        [p.photoDates?.after  ?? ''],
        });

        if (NAC_BLOCKS.has(block)) {
            this.editForm.get('gp')?.disable();
        }

        // Load existing project photos
        const photos = p.projectPhotos ?? { before: [], during: [], after: [] };
        this.photoBeforeExisting.set(photos.before ?? []);
        this.photoDuringExisting.set(photos.during ?? []);
        this.photoAfterExisting.set(photos.after  ?? []);

        this.saveError.set(null);
        this.isEditMode.set(true);
    }

    cancelEdit(): void {
        this.clearPhotoState();
        this.editExtraYear.set(null);
        this.isEditMode.set(false);
    }

    async saveEdit(): Promise<void> {
        if (this.editForm.invalid) { this.editForm.markAllAsTouched(); return; }
        this.isSaving.set(true);
        this.saveError.set(null);
        try {
            const v = this.editForm.getRawValue();

            // Upload any new staged photos
            const allNewPhotos = [
                ...this.photoBeforeFiles().map(f => ({ cat: 'before' as const, f })),
                ...this.photoDuringFiles().map(f => ({ cat: 'during' as const, f })),
                ...this.photoAfterFiles().map(f => ({ cat: 'after'  as const, f })),
            ];

            const beforeUrls = [...this.photoBeforeExisting()];
            const duringUrls = [...this.photoDuringExisting()];
            const afterUrls  = [...this.photoAfterExisting()];

            if (allNewPhotos.length) {
                this.isUploadingPhotos.set(true);
                this.photoUploadProgress.set(0);
                for (let i = 0; i < allNewPhotos.length; i++) {
                    const { cat, f } = allNewPhotos[i];
                    const url = await this.photoUploadService.uploadPhoto(
                        `${this.projectId}/${cat}`, f
                    );
                    if (cat === 'before') beforeUrls.push(url);
                    else if (cat === 'during') duringUrls.push(url);
                    else afterUrls.push(url);
                    this.photoUploadProgress.set(Math.round(((i + 1) / allNewPhotos.length) * 100));
                }
                this.isUploadingPhotos.set(false);
            }

            const updates: Record<string, any> = {
                name:                  v.name,
                scheme:                v.scheme ?? '',
                loc:                   v.block ?? '',
                block:                 v.block ?? '',
                gp:                    v.gp ?? '',
                status:                v.status,
                cost:                  Number(v.cost)  || 0,
                spent:                 Number(v.spent) || 0,
                yearOfCreation:        v.yearOfCreation ?? '',
                roadLength:            v.roadLength != null && v.roadLength !== '' ? Number(v.roadLength) : null,
                executant:             { name: v.executantName ?? '', contact: v.executantContact ?? '' },
                executorName:          v.executantName  ?? '',
                executorNumber:        v.executantContact ?? '',
                projectPhotos:         { before: beforeUrls, during: duringUrls, after: afterUrls },
                photoDates: {
                    before: v.photoDateBefore || null,
                    during: v.photoDateDuring || null,
                    after:  v.photoDateAfter  || null,
                },
                hmPriority:            !!v.hmPriority,
                recurringIntervention: !!v.recurringIntervention,
                isDelayed:             !!v.isDelayed,
                delayedReason:         v.delayedReason ?? '',
                // Always record who last set the delayed flag and when
                ...(!!v.isDelayed ? {
                    delayedDate: new Date().toISOString().split('T')[0],
                    delayedBy:   this.authService.currentUser()?.displayName
                                 || this.authService.currentUser()?.email
                                 || 'OSD',
                } : {}),
            };

            if (this.isFirestoreDoc()) {
                await this.firestoreService.updateProject(this.projectId, updates as any);
            } else {
                await this.firestoreService.setProject(this.projectId, { ...this.project()!, ...updates } as any);
                this.isFirestoreDoc.set(true);
            }

            this.clearPhotoState();
            this.isEditMode.set(false);
            this.saveSuccess.set(true);
            setTimeout(() => this.saveSuccess.set(false), 3000);
        } catch (err) {
            this.saveError.set('Failed to save. Please try again.');
            console.error(err);
        } finally {
            this.isSaving.set(false);
            this.isUploadingPhotos.set(false);
        }
    }

    // ── Photo management (edit mode) ──────────────────────────────────────
    onPhotoSelected(category: 'before' | 'during' | 'after', event: Event): void {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files ?? []);
        input.value = '';
        if (!files.length) return;
        const allowed = ['image/jpeg', 'image/png', 'image/webp'];
        const valid = files.filter(f => allowed.includes(f.type) && f.size <= 5 * 1024 * 1024);
        if (valid.length < files.length) alert('Some files skipped — only JPG/PNG/WEBP under 5 MB allowed.');

        const filesSignal    = category === 'before' ? this.photoBeforeFiles    : category === 'during' ? this.photoDuringFiles    : this.photoAfterFiles;
        const dataUrlsSignal = category === 'before' ? this.photoBeforeDataUrls : category === 'during' ? this.photoDuringDataUrls : this.photoAfterDataUrls;
        filesSignal.update(arr => [...arr, ...valid]);
        valid.forEach(file => {
            const reader = new FileReader();
            reader.onload = e => dataUrlsSignal.update(arr => [...arr, e.target?.result as string]);
            reader.readAsDataURL(file);
        });
    }

    removePhoto(category: 'before' | 'during' | 'after', index: number): void {
        const existingSignal = category === 'before' ? this.photoBeforeExisting : category === 'during' ? this.photoDuringExisting : this.photoAfterExisting;
        const filesSignal    = category === 'before' ? this.photoBeforeFiles    : category === 'during' ? this.photoDuringFiles    : this.photoAfterFiles;
        const dataUrlsSignal = category === 'before' ? this.photoBeforeDataUrls : category === 'during' ? this.photoDuringDataUrls : this.photoAfterDataUrls;
        const existingCount = existingSignal().length;
        if (index < existingCount) {
            existingSignal.update(arr => arr.filter((_, i) => i !== index));
        } else {
            const si = index - existingCount;
            filesSignal.update(arr => arr.filter((_, i) => i !== si));
            dataUrlsSignal.update(arr => arr.filter((_, i) => i !== si));
        }
    }

    private clearPhotoState(): void {
        this.photoBeforeFiles.set([]);
        this.photoDuringFiles.set([]);
        this.photoAfterFiles.set([]);
        this.photoBeforeDataUrls.set([]);
        this.photoDuringDataUrls.set([]);
        this.photoAfterDataUrls.set([]);
        this.photoBeforeExisting.set([]);
        this.photoDuringExisting.set([]);
        this.photoAfterExisting.set([]);
        this.isUploadingPhotos.set(false);
        this.photoUploadProgress.set(0);
    }

    // ── Delete ────────────────────────────────────────────────────────────
    confirmDelete(): void { this.showDeleteConfirm.set(true); }
    cancelDelete(): void  { this.showDeleteConfirm.set(false); }

    async deleteProject(): Promise<void> {
        this.isDeleting.set(true);
        try {
            await this.firestoreService.deleteProject(this.projectId);
            this.showDeleteConfirm.set(false);
            this.close();
        } catch (err) {
            console.error('Delete failed:', err);
            this.isDeleting.set(false);
        }
    }

    close(): void {
        this.location.back();
    }

    // ── Photo management (view mode — legacy flat photos array) ───────────
    async onPhotosChanged(newPhotos: string[]): Promise<void> {
        this.photoSaveError.set(null);
        this.project.update(p => p ? { ...p, photos: newPhotos } : p);
        try {
            if (this.isFirestoreDoc()) {
                await this.firestoreService.updateProject(this.projectId, { photos: newPhotos });
            } else {
                await this.firestoreService.setProject(this.projectId, { ...this.project()!, photos: newPhotos });
                this.isFirestoreDoc.set(true);
            }
        } catch (err) {
            console.error('Failed to save photos:', err);
            this.project.update(p => p ? { ...p, photos: newPhotos.slice(0, -1) } : p);
            this.photoSaveError.set('Failed to save photo changes. Please try again.');
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
